import {gql} from '@apollo/client';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  DEFAULT_MY_COLLECTION,
  DEFAULT_ORG_COLLECTION,
  FrontendLabelingStrategy,
  isRegistrySupportedType,
  LabelingStrategy,
  NavStoreRegistry,
  RegistryLabelingStrategy,
  RegistryPreferences,
  RegistrySummary,
  SAVING_STATES,
} from 'constants/RegistryConstants';
import * as SourceConstants from 'constants/SourceConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import preferences from 'Preferences';
import {
  UseIsRegistryWritableQuery,
  UseIsRegistryWritableQueryVariables,
} from 'registry/__generated/UseIsRegistryWritableQuery';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import EntityStore from 'stores/EntityStore';
import APIUtil from 'util/APIUtil';

import {ValidationErrorData} from './RegistryValidatorStore';

export function parseFolderValue(folderId: string | null): {
  folderId: string | null;
  registryId: string | null;
  tagSchemaId: string | null;
} {
  let registryId;
  let tagSchemaId;

  if (!folderId) {
    // If folderId is falsey (e.g. empty string), fallback to null (see BNCH-10194)
    return {folderId: null, registryId: null, tagSchemaId: null};
  }

  assert(typeof folderId === 'string', 'folderId must be a string', {folderId});

  if (folderId.indexOf(',') > -1) {
    [folderId, registryId, tagSchemaId] = folderId.split(',');
    return {folderId, registryId, tagSchemaId};
  }
  return {folderId, registryId: null, tagSchemaId: null};
}

export function generateFolderValue(
  folderId: string,
  registryId: string | undefined,
  tagSchemaId: string
): string {
  return `${folderId},${registryId},${tagSchemaId}`;
}

export function hasRegistrySupport(fileId: string): boolean {
  return isRegistrySupportedType(APIUtil.apiIdToType(fileId));
}

export function getRegistrySettingsUrl(ownerUrlId: string, registryId: string): string {
  return getRegistrySchemasSettingsUrl(ownerUrlId, registryId);
}

export function getRegistryGeneralSettingsUrl(ownerUrlId: string, registryId: string): string {
  return `/${ownerUrlId}/registries/${registryId}/general/`;
}

export function getRegistrySchemasSettingsUrl(ownerUrlId: string, registryId: string): string {
  return `/${ownerUrlId}/registries/${registryId}/tag-schemas/`;
}

export function getRegistryContainerSchemasUrl(ownerUrlId: string, registryId: string): string {
  return `/${ownerUrlId}/registries/${registryId}/container-schemas/`;
}

export function getRegistryBoxSchemasUrl(ownerUrlId: string, registryId: string): string {
  return `/${ownerUrlId}/registries/${registryId}/box-schemas/`;
}

export function getCreateBoxSchemaUrl(ownerUrlId: string, registryId: string): string {
  return `${getRegistryBoxSchemasUrl(ownerUrlId, registryId)}create`;
}

export function getBoxSchemaSettingsUrl(ownerUrlId: string, registryId: string, boxSchemaId: string): string {
  return `${getRegistryBoxSchemasUrl(ownerUrlId, registryId)}${boxSchemaId}`;
}

export function getRegistryPlateSchemasUrl(ownerUrlId: string, registryId: string): string {
  return `/${ownerUrlId}/registries/${registryId}/plate-schemas/`;
}

export function getCreatePlateSchemaUrl(ownerUrlId: string, registryId: string): string {
  return `${getRegistryPlateSchemasUrl(ownerUrlId, registryId)}create`;
}

export function getPlateSchemaSettingsUrl(
  ownerUrlId: string,
  registryId: string,
  plateSchemaId: string
): string {
  return `${getRegistryPlateSchemasUrl(ownerUrlId, registryId)}${plateSchemaId}`;
}

// TODO(T10317): Remove grid schema methods
export function getRegistryGridSchemasUrl(
  ownerUrlId: string,
  registryId: string,
  gridSchemaClass: string
): string {
  if (gridSchemaClass === ModelTypeConstants.BOX_SCHEMA) {
    return getRegistryBoxSchemasUrl(ownerUrlId, registryId);
  } else {
    assert(gridSchemaClass === ModelTypeConstants.PLATE_SCHEMA, 'Invalid grid schema class');
    return getRegistryPlateSchemasUrl(ownerUrlId, registryId);
  }
}

// TODO(T10317): Remove grid schema methods
export function getCreateGridSchemaUrl(
  ownerUrlId: string,
  registryId: string,
  gridSchemaClass: string
): string {
  if (gridSchemaClass === ModelTypeConstants.BOX_SCHEMA) {
    return getCreateBoxSchemaUrl(ownerUrlId, registryId);
  } else {
    assert(gridSchemaClass === ModelTypeConstants.PLATE_SCHEMA, 'Invalid grid schema class');
    return getCreatePlateSchemaUrl(ownerUrlId, registryId);
  }
}

// TODO(T10317): Remove grid schema methods
export function getGridSchemaSettingsUrl(
  ownerUrlId: string,
  registryId: string,
  gridSchemaId: string,
  gridSchemaClass: string
): string {
  if (gridSchemaClass === ModelTypeConstants.BOX_SCHEMA) {
    return getBoxSchemaSettingsUrl(ownerUrlId, registryId, gridSchemaId);
  } else {
    assert(gridSchemaClass === ModelTypeConstants.PLATE_SCHEMA, 'Invalid grid schema class');
    return getPlateSchemaSettingsUrl(ownerUrlId, registryId, gridSchemaId);
  }
}

export function getRegistryLocationSchemasUrl(ownerUrlId: string, registryId: string): string {
  return `/${ownerUrlId}/registries/${registryId}/location-schemas/`;
}

export function getCreateLocationSchemaUrl(ownerUrlId: string, registryId: string): string {
  return `${getRegistryLocationSchemasUrl(ownerUrlId, registryId)}create`;
}

export function getLocationSchemaSettingsUrl(
  ownerUrlId: string,
  registryId: string,
  locationSchemaId: string
): string {
  const locationSchemasUrl = getRegistryLocationSchemasUrl(ownerUrlId, registryId);
  return `${locationSchemasUrl}${locationSchemaId}`;
}

export function getStorableSchemaSettingsUrl(
  ownerUrlId: string,
  registryId: string,
  storableSchemaId: string
): string {
  if (APIUtil.isBoxSchema(storableSchemaId)) {
    return getBoxSchemaSettingsUrl(ownerUrlId, registryId, storableSchemaId);
  } else if (APIUtil.isContainerSchema(storableSchemaId)) {
    return getRegistryContainerSchemasUrl(ownerUrlId, registryId);
  } else if (APIUtil.isLocationSchema(storableSchemaId)) {
    return getLocationSchemaSettingsUrl(ownerUrlId, registryId, storableSchemaId);
  } else {
    assert(APIUtil.isPlateSchema(storableSchemaId), `Unexpected storable schema id ${storableSchemaId}`);
    return getPlateSchemaSettingsUrl(ownerUrlId, registryId, storableSchemaId);
  }
}

export function getRegistryLabelTemplatesUrl(ownerUrlId: string, registryId: string): string {
  return `/${ownerUrlId}/registries/${registryId}/label-templates/`;
}

export function getCreateLabelTemplateUrl(ownerUrlId: string, registryId: string): string {
  return `${getRegistryLabelTemplatesUrl(ownerUrlId, registryId)}create`;
}

export function getLabelTemplateSettingsUrl(
  ownerUrlId: string,
  registryId: string,
  labelTemplateId: string
): string {
  return `${getRegistryLabelTemplatesUrl(ownerUrlId, registryId)}${labelTemplateId}`;
}

export function getRegistryPrintersUrl(ownerUrlId: string, registryId: string): string {
  return `/${ownerUrlId}/registries/${registryId}/printers/`;
}

export function getCreatePrinterUrl(ownerUrlId: string, registryId: string): string {
  return `${getRegistryPrintersUrl(ownerUrlId, registryId)}create`;
}

export function getPrinterSettingsUrl(ownerUrlId: string, registryId: string, printerId: string): string {
  return `${getRegistryPrintersUrl(ownerUrlId, registryId)}${printerId}`;
}

export function getTagSchemaSettingsUrl(ownerUrlId: string, registryId: string, tagSchemaId: string): string {
  return `${getRegistrySchemasSettingsUrl(ownerUrlId, registryId)}${tagSchemaId}`;
}

export function getCreateTagSchemaUrl(ownerUrlId: string, registryId: string): string {
  return `${getRegistrySchemasSettingsUrl(ownerUrlId, registryId)}create`;
}

export function getRegistrySelectorsSettingsUrl(ownerUrlId: string, registryId: string): string {
  return `/${ownerUrlId}/registries/${registryId}/dropdowns/`;
}

export function getSchemaFieldSelectorSettingsUrl(
  ownerUrlId: string,
  registryId: string,
  selectorId: string
): string {
  return `/${ownerUrlId}/registries/${registryId}/dropdowns/${selectorId}`;
}

export function getCreateSchemaFieldSelectorUrl(ownerUrlId: string, registryId: string): string {
  return `/${ownerUrlId}/registries/${registryId}/dropdowns/create`;
}

export function getWarningCodesFromValidationResponse(validationResponse: {
  [fileId: string]: ValidationErrorData;
}): {[fileId: string]: string} {
  const warningCodes: {[fileId: string]: string} = {};
  for (const fileId of Object.keys(validationResponse)) {
    warningCodes[fileId] = validationResponse[fileId].warningCode;
  }
  return warningCodes;
}

export function getDuplicateItemApiIdsFromValidationResponse(validationResponse: {
  [fileId: string]: ValidationErrorData;
}): {[fileId: string]: Array<string>} {
  const duplicateApiIds: {[fileId: string]: Array<string>} = {};
  for (const fileId of Object.keys(validationResponse)) {
    duplicateApiIds[fileId] = validationResponse[fileId].duplicateItemApiIds ?? [];
  }
  return duplicateApiIds;
}

export function getUniqueDuplicateItems(duplicateItemApiIds: {
  [fileId: string]: Array<string>;
}): Array<string> {
  return [...new Set(Object.values(duplicateItemApiIds).flat())];
}

export function isRegistry(source?: {sourceType: string}): boolean {
  return source != null && source.sourceType === SourceConstants.REGISTRY_TYPE;
}

export const isRegistryFolder = (folder: {source?: SourceConstants.MinimalSourceSummary}): boolean =>
  Boolean(folder.source && isRegistry(folder.source));

export const isRegistered = (entity: {folder: {source?: {sourceType: string}}}): boolean => {
  const source = entity.folder.source;
  return Boolean(source) && isRegistry(source);
};

export const getCollectionPreference = (): string | null =>
  preferences.get(RegistryPreferences.DEFAULT_COLLECTION);

export const setCollectionPreference = (collection: string): void => {
  preferences.set(RegistryPreferences.DEFAULT_COLLECTION, collection);
};

export function getDefaultCollectionId(registry: RegistrySummary): string {
  const orgCollectionId = registry.s_owner.id;
  const currentUser = EntityStore.currentUser();
  if (!currentUser) {
    return orgCollectionId;
  }
  const myCollectionId = EntityStore.currentUser().id;
  if (registry.disablePersonalCollections) {
    return orgCollectionId;
  }
  const collectionPreference = getCollectionPreference();
  if (!collectionPreference) {
    return myCollectionId;
  }
  switch (collectionPreference) {
    case DEFAULT_MY_COLLECTION:
      return myCollectionId;
    case DEFAULT_ORG_COLLECTION:
      return orgCollectionId;
    default:
      throw new Error('Invalid default collection preference');
  }
}

export function getErrorData(
  errors: ValidationErrorData,
  labelingStrategy: LabelingStrategy
): {errors: Array<string>; warnings: Array<string>} {
  switch (labelingStrategy) {
    case RegistryLabelingStrategy.NEW_IDS:
      return errors.existingNameErrors;
    case RegistryLabelingStrategy.IDS_FROM_NAMES:
      return errors.convertedNameErrors;
    case RegistryLabelingStrategy.DELETE_NAMES:
      return errors.deletedNameErrors;
    case RegistryLabelingStrategy.SET_FROM_NAME_PARTS:
      return errors.setNameFromPartsErrors;
    case RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS:
      return errors.replaceNamesFromPartsErrors;
    case RegistryLabelingStrategy.KEEP_NAMES:
      return errors.keepNameErrors;
    case RegistryLabelingStrategy.REPLACE_ID_AND_NAME_FROM_PARTS:
      return errors.replaceIdAndNameFromPartsErrors;
    case FrontendLabelingStrategy.CONVERT_IDS:
      return {errors: [], warnings: []};
    default:
      throw new Error(`Invalid labelingStrategy ${labelingStrategy}`);
  }
}

// TODO(BNCH-30720) Fix SchemaField/ClientSchemaField type issues
export function sanitizeFields(fields: Array<SchemaField | ClientSchemaField>): Array<SchemaField> {
  // eslint-disable-next-line no-restricted-syntax
  const namedFields = _.filter(fields, (field) => field.name.length > 0);
  // eslint-disable-next-line no-restricted-syntax
  const sanitizedFields = _.map(namedFields, (field) => _.omit(field, 'cid'));
  return sanitizedFields;
}

export function isSavingFinished(savingState: string): boolean {
  return [SAVING_STATES.SUCCESS, SAVING_STATES.FAILURE, SAVING_STATES.CREATE_SUCCESS].includes(savingState);
}

export function isNavStoreRegistry(registry: RegistrySummary): registry is NavStoreRegistry {
  return (registry as NavStoreRegistry).permissions != null;
}

export function hasSettingsAccess(registry: NavStoreRegistry): boolean {
  return registry.areSettingsAdminOnly ? registry.permissions.admin : registry.permissions.writable;
}

export const USE_IS_REGISTRY_WRITABLE_QUERY = gql`
  query UseIsRegistryWritableQuery($registryId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    registry(id: $registryId) {
      ...NavStoreRegistryGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('NavStoreRegistry').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function useIsRegistryWritableQuery(registryId: string): boolean {
  const result = useBenchlingQuery<UseIsRegistryWritableQuery, UseIsRegistryWritableQueryVariables>(
    USE_IS_REGISTRY_WRITABLE_QUERY,
    {variables: {registryId}}
  );
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Error when loading registry details. Please refresh the page.');
  }
  if (result.type === QueryResultType.ERROR || result.type === QueryResultType.PENDING) {
    return false;
  }
  assertAndContinue(!isInaccessible(result.data.registry), 'Expected registry to be accessible');
  return (
    !isInaccessible(result.data.registry) &&
    hasSettingsAccess(graphQLToREST('NavStoreRegistry', result.data.registry))
  );
}

export function formatSchemaNameAndType(schemaName: string, schemaTypeName: string): string {
  return `${schemaName} \u00b7 ${schemaTypeName}`;
}
